// Модель мира «Секундочка». Ядро: WorldState = f(t, edits).
//
// t          — непрерывное время мира (анимируется между моментами);
// момент     — целое 0..10 на часах мира (HORIZON);
// правка     — перемещение предмета пальцем в заморозке, привязанное к моменту.
//
// Состояние мира в любой момент k получается прогоном moments 0..k
// (см. world.ts → simulate). Ничего не хранится вне t и edits — поэтому
// откат «бесплатный» и полностью детерминированный.

export const HORIZON = 10;

/** Предметы, которые ребёнок может двигать пальцем в заморозке. */
export type DraggableId = "egg" | "fly" | "salad";

/** Куда положено яйцо. */
export type EggPlace = "moss" | "nest";

/** Положение мухи: у камыша (исходное), на листе кувшинки, или свободно бро­шена. */
export interface FlyPlacement {
  onLeaf: boolean;
  x: number;
  y: number;
}

/** Значение правки зависит от предмета. */
export type Placement =
  | { kind: "egg"; place: EggPlace }
  | { kind: "fly"; value: FlyPlacement }
  | { kind: "salad"; pebble: number };

/** Одна правка: «предмет, момент, новое положение». */
export interface Edit {
  tick: number; // момент, на котором сделана правка
  objectId: DraggableId;
  placement: Placement;
}

/** Событие момента — для звука (в т.ч. обратного), реакций и лога. */
export type MomentEventKind =
  | "tick" // щелчок стрелки
  | "snailStep"
  | "snailEat"
  | "snailHungry"
  | "frogJump"
  | "frogDive"
  | "frogCatch"
  | "henFly"
  | "henReturn"
  | "eggWarm"
  | "eggHatch"
  | "chickCry"
  | "chickUnderWing"
  | "flowerOpen"
  | "flowerClose"
  | "cuckoo";

export interface MomentEvent {
  kind: MomentEventKind;
  moment: number;
}

export interface SnailState {
  pebble: number; // 0..10 по тропинке из 11 камешков
  eating: boolean;
  hungrySleep: boolean;
}

export interface FrogState {
  place: "pond" | "leaf";
  hasFly: boolean;
}

export interface FlyState {
  place: "reeds" | "leaf" | "free";
  x: number;
  y: number;
  eaten: boolean;
}

export interface HenState {
  place: "nest" | "fence";
  wantsChick: boolean; // облачко с цыплёнком (пустое гнездо)
}

export interface EggState {
  place: EggPlace;
  warmth: number; // 0..5 точек тепла
  hatched: boolean;
  hatchedAt: number | null;
}

export interface ChickState {
  exists: boolean;
  crying: boolean;
  underWing: boolean;
  asleep: boolean;
}

export interface WorldState {
  moment: number; // k
  snail: SnailState;
  salad: { pebble: number };
  frog: FrogState;
  fly: FlyState;
  hen: HenState;
  egg: EggState;
  chick: ChickState;
  /** События, произошедшие при входе В этот момент (при счёте вперёд). */
  events: MomentEvent[];
}

/** Системные состояния (полудуплекс голоса + движок времени). */
export type SystemPhase =
  | "LISTENING" // слушаем ребёнка
  | "PROCESSING" // распознаём речь
  | "SPEAKING" // говорит Кукушка — STT игнорирует результаты
  | "MOVING" // мир идёт к цели
  | "FROZEN"; // тишина, можно двигать предметы
